import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { selectMatterById } from '@/matter/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectMedChronAssetForMatter } from '@/medical-chronology/asset/store/selectors'
import MedChronAssetChatButton from '@/medical-chronology/asset/components/MedChronAssetChatButton'
import MedChronAssetDownloadButton from '@/medical-chronology/asset/components/MedChronAssetDownloadButton'
import { matterPaths } from '@/routing/paths'
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'

/**
 * Breadcrumbs for the living Medical Chronology: Matter > Medical Chronology, with
 * Ask Assistant + Download actions once the chronology has events.
 */
export default function MatterBreadcrumbsMedChronAsset({ showAddFilesCrumb = false }: { showAddFilesCrumb?: boolean } = {}) {
  const navigate = useNavigate()

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: activeMatterId }))
  const asset = useAppSelector((state: RootState) => selectMedChronAssetForMatter(state, { matterId: activeMatterId ?? undefined }))

  const breadcrumbItems: BreadcrumbItemType[] = useMemo(() => {
    if (!initialLoadComplete) return []

    const items: BreadcrumbItemType[] = []
    if (matter) {
      items.push({
        id: `matter-${matter.id}`,
        label: matter.name,
        onClick: () => navigate(matterPaths.overview(matter.id)),
      })
    }
    items.push({
      id: 'med-chron',
      label: 'Medical Chronology',
      // On the Add Files sub-page, Medical Chronology becomes a clickable parent crumb.
      ...(showAddFilesCrumb && activeMatterId ? { onClick: () => navigate(matterPaths.chronology.index(activeMatterId)) } : {}),
    })
    if (showAddFilesCrumb) {
      items.push({ id: 'add-files', label: 'Add Files' })
    }
    return items
  }, [initialLoadComplete, matter, navigate, showAddFilesCrumb, activeMatterId])

  const showActions = asset && asset.total_events > 0 && activeMatterId

  return (
    // Negative margin is to make this match the design files / chat (due to sidebar closing button gap)
    <div className="h-[52px] lg:h-[60px] flex lg:-ml-3">
      <div className="w-full px-3 lg:pr-6 flex justify-between items-center border-b-[0.5px] bg-brand-neutral-100 lg:bg-brand-neutral-50">
        <div className="flex-1 min-w-0 overflow-hidden">
          <Breadcrumbs items={breadcrumbItems} maxLabelLength={30} maxActiveLabelLength={60} persistFirstItem />
        </div>

        {showActions && (
          <div className="hidden lg:flex items-center justify-center gap-3.5">
            <MedChronAssetChatButton assetId={asset.id} matterId={activeMatterId} />
            <MedChronAssetDownloadButton assetId={asset.id} />
          </div>
        )}
      </div>
    </div>
  )
}
